/**
 * Collection Explorer Tabs Component
 * Tab navigation with per-item theme support for arrows
 */
import { setupAdjacentSlidePreload } from "@/utils/adjacent-slide-preload.util";
const arrowButtonBase = "hidden lg:flex w-12 h-12 bg-white rounded-full shadow-lg items-center justify-center transition-all shrink-0 border border-gray-200 cursor-pointer";
const arrowThemeClasses = {
    orange: `${arrowButtonBase} hover:bg-orange text-orange hover:text-white`,
    yellow: `${arrowButtonBase} hover:bg-yellow text-black hover:text-black`,
    gray: `${arrowButtonBase} hover:bg-gray text-gray hover:text-white`,
    blue: `${arrowButtonBase} hover:bg-blue text-blue hover:text-white`,
};
function getArrowClass(theme) {
    return arrowThemeClasses[theme] ?? arrowThemeClasses.blue;
}
export function CollectionExplorerTabs(element) {
    const totalItems = parseInt(element.dataset.totalItems || "0", 10);
    let currentSlide = 0;
    const explorerTabs = element.querySelectorAll("[data-explorer-tab]");
    const explorerSlides = element.querySelectorAll("[data-explorer-slide]");
    const prevBtn = element.querySelector("[data-explorer-tab-prev]");
    const nextBtn = element.querySelector("[data-explorer-tab-next]");
    if (!explorerTabs.length || !explorerSlides.length) {
        console.warn("[CollectionExplorerTabs] Missing required elements. Component disabled.");
        return () => { };
    }
    const adjacentSlidePreload = setupAdjacentSlidePreload({
        element,
        slides: explorerSlides,
        totalItems,
        getCurrentIndex: () => currentSlide,
    });
    const getTabId = (index) => {
        const tab = explorerTabs[index];
        return tab ? tab.getAttribute("data-tab-id") : null;
    };
    const getIndexByTabId = (tabId) => {
        for (let i = 0; i < explorerTabs.length; i++) {
            if (explorerTabs[i].getAttribute("data-tab-id") === tabId) {
                return i;
            }
        }
        return -1;
    };
    const getTabFromHash = () => {
        const hash = window.location.hash;
        if (!hash)
            return -1;
        const params = new URLSearchParams(hash.substring(1));
        const tabId = params.get("explorer-tab");
        if (!tabId)
            return -1;
        return getIndexByTabId(tabId);
    };
    const updateHash = (index, pushState) => {
        const tabId = getTabId(index);
        if (!tabId)
            return;
        const params = new URLSearchParams(window.location.hash.substring(1));
        params.set("explorer-tab", tabId);
        const newHash = "#" + params.toString();
        if (pushState) {
            history.pushState(null, "", newHash);
        }
        else {
            history.replaceState(null, "", newHash);
        }
    };
    const updateArrowTheme = (index) => {
        const slide = explorerSlides[index];
        const theme = slide?.getAttribute("data-item-theme") || "blue";
        const arrowClass = getArrowClass(theme);
        if (prevBtn) {
            prevBtn.className = arrowClass;
        }
        if (nextBtn) {
            nextBtn.className = arrowClass;
        }
    };
    const goToSlide = (index, updateHistory = true) => {
        explorerTabs.forEach((tab) => {
            tab.setAttribute("data-active", "false");
            tab.setAttribute("aria-selected", "false");
        });
        explorerSlides.forEach((slide) => {
            slide.setAttribute("data-active", "false");
            slide.setAttribute("aria-hidden", "true");
        });
        const currentTab = explorerTabs[index];
        const currentSlideElement = explorerSlides[index];
        if (currentTab) {
            currentTab.setAttribute("data-active", "true");
            currentTab.setAttribute("aria-selected", "true");
        }
        if (currentSlideElement) {
            currentSlideElement.setAttribute("data-active", "true");
            currentSlideElement.setAttribute("aria-hidden", "false");
        }
        currentSlide = index;
        updateArrowTheme(index);
        adjacentSlidePreload.onSlideChanged(index);
        if (updateHistory) {
            updateHash(index, true);
        }
    };
    const nextSlide = () => {
        const nextIndex = (currentSlide + 1) % totalItems;
        goToSlide(nextIndex);
    };
    const prevSlide = () => {
        const prevIndex = (currentSlide - 1 + totalItems) % totalItems;
        goToSlide(prevIndex);
    };
    const onPopState = () => {
        const index = getTabFromHash();
        if (index >= 0 && index !== currentSlide) {
            goToSlide(index, false);
        }
    };
    const tabHandlers = new Map();
    const navigationHandlers = [];
    explorerTabs.forEach((tab, index) => {
        const handler = () => goToSlide(index);
        tabHandlers.set(tab, handler);
        tab.addEventListener("click", handler);
    });
    if (prevBtn) {
        const handler = () => prevSlide();
        navigationHandlers.push({ element: prevBtn, handler });
        prevBtn.addEventListener("click", handler);
    }
    if (nextBtn) {
        const handler = () => nextSlide();
        navigationHandlers.push({ element: nextBtn, handler });
        nextBtn.addEventListener("click", handler);
    }
    window.addEventListener("popstate", onPopState);
    const restoredIndex = getTabFromHash();
    if (restoredIndex >= 0) {
        goToSlide(restoredIndex, false);
    }
    else {
        goToSlide(0, false);
    }
    return () => {
        explorerTabs.forEach((tab) => {
            const handler = tabHandlers.get(tab);
            if (handler) {
                tab.removeEventListener("click", handler);
            }
        });
        tabHandlers.clear();
        navigationHandlers.forEach(({ element, handler }) => {
            element.removeEventListener("click", handler);
        });
        window.removeEventListener("popstate", onPopState);
        adjacentSlidePreload.destroy();
    };
}
